import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell, CreditCard, LogOut, Plug, Settings, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { useAuth } from '@/features/auth/session';
import { logger } from '@/shared/observability/logger';

/** Initials for the avatar, from whatever the account actually has. */
function initialsOf(firstName?: string | null, lastName?: string | null, email?: string): string {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.trim();
  if (initials) {
    return initials.toUpperCase();
  }
  // An account with no profile still has an address, and a blank circle beside a
  // blank name reads as a broken avatar rather than an incomplete profile.
  return (email?.[0] ?? '?').toUpperCase();
}

const ITEM =
  'flex cursor-pointer items-center gap-3 px-4 py-3 text-sm outline-none ' +
  'text-gray-700 hover:bg-gray-50 focus:bg-gray-50 ' +
  'dark:text-gray-200 dark:hover:bg-white/5 dark:focus:bg-white/5';

/**
 * The account control in the top-right corner, and the menu behind it.
 *
 * <h2>Every colour here has a dark variant, deliberately</h2>
 *
 * The version this replaces used `bg-[#f7fafd]` and `border-[#8ec5ff]` — arbitrary
 * values, which Tailwind emits verbatim and the skin has no way to override. On
 * the dark theme that made this the one white object on the page, with the name
 * inside it rendering near-white on near-white because `text-gray-900` *is*
 * overridden in dark. Measured contrast 1.03: the name was not dim, it was gone.
 *
 * <h2>The trigger carries `nav-link-button`, and that is not decoration</h2>
 *
 * `index.css` carries `.dark button:not(…fourteen classes…)` with `!important` on `color`
 * and `border-color`, so a plain `<button>` on the dark theme is repainted by the skin
 * whatever it asks for. `nav-link-button` is one of the fourteen, and gets
 * `background-color: #262626 !important` of its own — which is why the design's
 * `bg-[#dbe7fd]` needs no dark variant beside it. Copied from
 * `the previous product's AppNav.tsx:1889`.
 *
 * <h2>The trigger is the avatar and nothing else</h2>
 *
 * A 36px circle of initials, which is the source's. It was a pill — the circle, the name
 * truncated to fifteen characters, and a chevron — until 8 October 2026, and it was the
 * widest object in the bar: the name is now two rows below it at the foot of the rail, so
 * the pill was the same name twice on one screen, once at the cost of 160px of header.
 *
 * <h2>Sign out goes through `useAuth`</h2>
 *
 * Not `tokenStorage.clear()`. Signing out revokes the token server-side before
 * forgetting it locally — clearing storage alone leaves it working for anybody
 * holding a copy until it expires.
 */
export function AccountMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || (user?.email ?? '');

  const onSignOut = () => {
    void signOut()
      .catch((error: unknown) => {
        // A failed revoke still ends the local session -- refusing to sign
        // somebody out because the network is down is the worst of both.
        logger.warn('sign-out could not revoke the token', { error });
      })
      .finally(() => {
        void navigate(ROUTES.signIn);
      });
  };

  return (
    // Not modal: a modal menu locks the page's scroll while it is open, and the bar's thumb and the wheel
    // both stop working behind a small menu. This one has nothing to protect the page from; it closes on
    // an outside click or Escape either way.
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="nav-link-button flex h-[36px] w-[36px] items-center justify-center overflow-hidden rounded-full bg-[#dbe7fd] p-0 text-[15px] font-semibold text-blue-700 hover:bg-blue-200 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:text-blue-300"
        >
          {initialsOf(user?.firstName, user?.lastName, user?.email)}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 w-64 overflow-hidden rounded-xl border border-blue-100 bg-white shadow-xl dark:border-white/10 dark:bg-[#0f1e3d]"
        >
          <div className="border-b border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 dark:border-white/10 dark:bg-none dark:bg-white/5">
            <div className="font-medium text-gray-900 dark:text-gray-50">{displayName}</div>
            <div className="truncate text-sm text-gray-500 dark:text-gray-400">
              {user?.email ?? ''}
            </div>
          </div>

          {/*
            Added 8 October 2026, when the sidebar redesign took the "My Account" row off the
            rail. The screen did not move and `/account` is unchanged -- only the way in did,
            which is why this item exists rather than the route being deleted with the row.
          */}
          <DropdownMenu.Item className={ITEM} onSelect={() => void navigate(ROUTES.account)}>
            <Settings aria-hidden className="h-4 w-4" />
            My Account
          </DropdownMenu.Item>

          <DropdownMenu.Item className={ITEM} onSelect={() => void navigate(ROUTES.profile)}>
            <User aria-hidden className="h-4 w-4" />
            My Profile
          </DropdownMenu.Item>

          <DropdownMenu.Item className={ITEM} onSelect={() => void navigate(ROUTES.billing)}>
            <CreditCard aria-hidden className="h-4 w-4" />
            Billing &amp; Plans
          </DropdownMenu.Item>

          <DropdownMenu.Item className={ITEM} onSelect={() => void navigate(ROUTES.integrations)}>
            <Plug aria-hidden className="h-4 w-4" />
            Integrations
          </DropdownMenu.Item>

          <DropdownMenu.Item className={ITEM} onSelect={() => void navigate(ROUTES.notifications)}>
            <Bell aria-hidden className="h-4 w-4" />
            Notifications
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="h-px bg-gray-100 dark:bg-white/10" />

          <DropdownMenu.Item
            className={
              'flex cursor-pointer items-center gap-3 px-4 py-3 text-sm text-red-600 outline-none ' +
              'hover:bg-red-50 focus:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10'
            }
            onSelect={onSignOut}
          >
            <LogOut aria-hidden className="h-4 w-4" />
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
